import {
  type BulkResult,
  type Paginated,
  TASK_STATUS_LABELS,
  TASK_STATUSES,
  TASK_VIEW_LABELS,
  TASK_VIEWS,
  type TaskListItem,
  type TaskSort,
  type TaskStatus,
  type TaskView,
} from '@erp/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import clsx from 'clsx';
import {
  CalendarRange,
  CheckSquare,
  Columns3,
  List,
  MessageSquare,
  Plus,
  Repeat,
  Search,
} from 'lucide-react';
import { type DragEvent, useId, useRef, useState } from 'react';
import {
  BulkBar,
  BulkNotice,
  type ColumnDef,
  ColumnsMenu,
  type ColumnsState,
  cleanParams,
  countActive,
  DateRange,
  ExportButton,
  FilterField,
  FilterPanel,
  FilterToggle,
  PresetChips,
  SavedFilters,
  SelectBox,
  SortHeader,
  type SortState,
  useColumns,
  useSelection,
} from '@/components/list-tools';
import { Deadline, PriorityMark, STATUS_TONE, StatusBadge } from '@/components/task-bits';
import { Avatar, Button, ErrorBox, Input, PageHeader, Select, Spinner } from '@/components/ui';
import { api } from '@/lib/api';
import { useCan } from '@/lib/auth';
import { formatMoment, plural } from '@/lib/format';
import {
  assigneesQuery,
  projectsQuery,
  type TasksFilter,
  taskCountersQuery,
  tasksQuery,
} from '@/lib/queries';
import { TaskFormDialog } from './task-form';
import { DeadlineBoard } from './tasks-deadline';

export const TASK_PRESETS = [
  'active',
  'overdue',
  'soon',
  'unread',
  'done',
  'deferred',
  'nodeadline',
] as const;
export type TaskPreset = (typeof TASK_PRESETS)[number];

export interface TasksSearch {
  view?: TaskView;
  mode?: 'board' | 'list' | 'deadline';
  project?: string;
  q?: string;
  preset?: TaskPreset;
  creator?: string;
  assignee?: string;
  deadlineFrom?: string;
  deadlineTo?: string;
  createdFrom?: string;
  createdTo?: string;
  closedFrom?: string;
  closedTo?: string;
  sort?: TaskSort;
  dir?: 'asc' | 'desc';
}

/** Готовый фильтр → условия запроса. */
function presetFilter(preset: TaskPreset | undefined): Partial<TasksFilter> {
  switch (preset) {
    case 'active':
      return { status: 'new,in_progress,review' };
    case 'overdue':
      return { overdue: true };
    case 'soon':
      return { dueSoon: true };
    case 'unread':
      return { unread: true };
    case 'done':
      return { status: 'done' };
    case 'deferred':
      return { status: 'deferred' };
    case 'nodeadline':
      return { noDeadline: true, status: 'new,in_progress,review' };
    default:
      return {};
  }
}

/** Всё, что задаёт фильтр (без вида «канбан/список») — это и сохраняется в «Мои фильтры». */
const FILTER_KEYS = [
  'view',
  'project',
  'q',
  'preset',
  'creator',
  'assignee',
  'deadlineFrom',
  'deadlineTo',
  'createdFrom',
  'createdTo',
  'closedFrom',
  'closedTo',
  'sort',
  'dir',
] as const;

export function TasksPage() {
  const search = useSearch({ strict: false }) as TasksSearch;
  const navigate = useNavigate();
  const can = useCan();
  const view = search.view ?? 'doing';
  const mode = search.mode ?? 'board';
  const [q, setQ] = useState(search.q ?? '');
  const [creating, setCreating] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const debounce = useRef<number | undefined>(undefined);
  const counters = useQuery(taskCountersQuery);
  const projects = useQuery(projectsQuery);
  const people = useQuery(assigneesQuery);
  const panelId = useId();
  const cols = useColumns('tasks', TASK_COLUMNS);

  const filter: TasksFilter = {
    view,
    projectId: search.project,
    q: search.q,
    creatorId: search.creator,
    assigneeId: search.assignee,
    deadlineFrom: search.deadlineFrom,
    deadlineTo: search.deadlineTo,
    createdFrom: search.createdFrom,
    createdTo: search.createdTo,
    closedFrom: search.closedFrom,
    closedTo: search.closedTo,
    ...presetFilter(search.preset),
    ...(mode === 'board'
      ? { order: 'board' }
      : mode === 'deadline'
        ? // «Сроки» — только незакрытые задачи (если готовый фильтр не задал статус сам)
          { order: 'deadline', status: presetFilter(search.preset).status ?? 'new,in_progress,review' }
        : { order: 'deadline', sort: search.sort, dir: search.dir }),
  };
  const list = useQuery(tasksQuery(filter));

  const current = cleanParams(Object.fromEntries(FILTER_KEYS.map((k) => [k, search[k]])));
  const setSearch = (patch: Partial<TasksSearch>) =>
    navigate({ to: '/tasks', search: { ...search, ...patch }, replace: true });

  const onQuery = (value: string) => {
    setQ(value);
    window.clearTimeout(debounce.current);
    debounce.current = window.setTimeout(() => setSearch({ q: value.trim() || undefined }), 250);
  };

  const panelCount = countActive([
    search.creator,
    search.assignee,
    search.project,
    search.deadlineFrom || search.deadlineTo,
    search.createdFrom || search.createdTo,
    search.closedFrom || search.closedTo,
  ]);
  const resetPanel = () =>
    setSearch({
      creator: undefined,
      assignee: undefined,
      project: undefined,
      deadlineFrom: undefined,
      deadlineTo: undefined,
      createdFrom: undefined,
      createdTo: undefined,
      closedFrom: undefined,
      closedTo: undefined,
    });

  const c = counters.data;
  const summary = c
    ? [
        `${c.doing} в работе у вас`,
        c.overdue ? `${c.overdue} ${plural(c.overdue, ['просрочена', 'просрочены', 'просрочено'])}` : null,
        c.review ? `${c.review} ждут вашей проверки` : null,
      ]
        .filter(Boolean)
        .join(', ')
    : undefined;

  return (
    <div>
      <PageHeader
        title="Задачи"
        description={summary}
        actions={
          <>
            <fieldset className="inline-flex rounded-md border border-line-strong bg-surface p-0.5">
              <legend className="sr-only">Вид</legend>
              {(
                [
                  ['board', 'Канбан', Columns3],
                  ['list', 'Список', List],
                  ['deadline', 'Сроки', CalendarRange],
                ] as const
              ).map(([m, label, Icon]) => (
                <button
                  key={m}
                  type="button"
                  aria-pressed={mode === m}
                  onClick={() => setSearch({ mode: m === 'board' ? undefined : m })}
                  className={clsx(
                    'inline-flex h-8 items-center gap-1.5 rounded px-3 text-[13.5px]',
                    mode === m ? 'bg-graphite text-white' : 'text-ink-soft hover:bg-sunken',
                  )}
                >
                  <Icon className="size-4" aria-hidden />
                  {label}
                </button>
              ))}
            </fieldset>
            <Link
              to="/tasks/recurring"
              className="inline-flex h-9 items-center gap-2 rounded-[var(--radius-control)] border border-line-strong bg-surface px-3 text-[13.5px] font-medium hover:bg-sunken"
            >
              <Repeat className="size-4" aria-hidden />
              Повторяющиеся
            </Link>
            {can('tasks.task.create') && (
              <Button onClick={() => setCreating(true)}>
                <Plus className="size-4" aria-hidden />
                Новая задача
              </Button>
            )}
          </>
        }
      />
      {creating && <TaskFormDialog defaultProjectId={search.project} onClose={() => setCreating(false)} />}

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div
          role="tablist"
          aria-label="Чьи задачи"
          className="inline-flex max-w-full overflow-x-auto rounded-md border border-line-strong bg-surface p-0.5"
        >
          {TASK_VIEWS.map((v) => (
            <button
              key={v}
              role="tab"
              type="button"
              aria-selected={view === v}
              onClick={() => setSearch({ view: v === 'doing' ? undefined : v })}
              className={clsx(
                'h-8 shrink-0 rounded px-3 text-[13.5px] whitespace-nowrap',
                view === v ? 'bg-graphite text-white' : 'text-ink-soft hover:bg-sunken',
              )}
            >
              {TASK_VIEW_LABELS[v]}
              {v === 'doing' && c?.doing ? (
                <span className="ml-1.5 tabular-nums opacity-70">{c.doing}</span>
              ) : null}
            </button>
          ))}
        </div>

        <div className="relative w-full max-w-[240px]">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-steel"
            aria-hidden
          />
          <Input
            type="search"
            value={q}
            onChange={(e) => onQuery(e.target.value)}
            placeholder="Название или номер"
            aria-label="Поиск задач"
            className="pl-9"
          />
        </div>

        <FilterToggle
          open={filterOpen || panelCount > 0}
          active={panelCount}
          controls={panelId}
          onToggle={() => setFilterOpen((v) => !v)}
        />
        <SavedFilters
          page="tasks"
          current={current}
          onApply={(p) =>
            navigate({ to: '/tasks', search: { mode: search.mode, ...(p as TasksSearch) }, replace: true })
          }
        />
        {mode === 'list' && <ColumnsMenu state={cols} />}
        <ExportButton path="/tasks/export.xlsx" query={{ ...filter, order: 'deadline' }} />
      </div>

      <PresetChips
        label="Готовые фильтры"
        value={search.preset}
        onChange={(preset) => setSearch({ preset })}
        options={[
          { value: 'active', label: 'В работе' },
          { value: 'overdue', label: 'Просрочены', count: c?.overdue, tone: 'danger' },
          { value: 'soon', label: 'Почти просрочены' },
          { value: 'unread', label: 'Новые комментарии', count: c?.unreadComments, tone: 'signal' },
          { value: 'nodeadline', label: 'Без срока' },
          { value: 'done', label: 'Завершены' },
          { value: 'deferred', label: 'Отложены' },
        ]}
      />

      {(filterOpen || panelCount > 0) && (
        <div className="mt-3">
          <FilterPanel id={panelId} onReset={resetPanel}>
            <FilterField label="Постановщик">
              {(id) => (
                <Select
                  id={id}
                  value={search.creator ?? ''}
                  onChange={(e) => setSearch({ creator: e.target.value || undefined })}
                >
                  <option value="">Любой</option>
                  {people.data?.map((p) => (
                    <option key={p.userId} value={p.userId}>
                      {p.fullName}
                    </option>
                  ))}
                </Select>
              )}
            </FilterField>
            <FilterField label="Исполнитель">
              {(id) => (
                <Select
                  id={id}
                  value={search.assignee ?? ''}
                  onChange={(e) => setSearch({ assignee: e.target.value || undefined })}
                >
                  <option value="">Любой</option>
                  {people.data?.map((p) => (
                    <option key={p.userId} value={p.userId}>
                      {p.fullName}
                    </option>
                  ))}
                </Select>
              )}
            </FilterField>
            <FilterField label="Проект">
              {(id) => (
                <Select
                  id={id}
                  value={search.project ?? ''}
                  onChange={(e) => setSearch({ project: e.target.value || undefined })}
                >
                  <option value="">Все проекты</option>
                  {projects.data?.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </Select>
              )}
            </FilterField>
            <DateRange
              label="Срок"
              from={search.deadlineFrom}
              to={search.deadlineTo}
              onChange={(deadlineFrom, deadlineTo) => setSearch({ deadlineFrom, deadlineTo })}
            />
            <DateRange
              label="Создана"
              from={search.createdFrom}
              to={search.createdTo}
              onChange={(createdFrom, createdTo) => setSearch({ createdFrom, createdTo })}
            />
            <DateRange
              label="Завершена"
              from={search.closedFrom}
              to={search.closedTo}
              onChange={(closedFrom, closedTo) => setSearch({ closedFrom, closedTo })}
            />
          </FilterPanel>
        </div>
      )}

      <div className="mt-4">
        {list.isPending ? (
          <Spinner />
        ) : list.error ? (
          <ErrorBox error={list.error} />
        ) : mode === 'board' ? (
          <Board items={list.data.items} filter={filter} />
        ) : mode === 'deadline' ? (
          <DeadlineBoard items={list.data.items} filter={filter} />
        ) : list.data.items.length === 0 ? (
          <EmptyState
            view={view}
            filtered={Object.keys(current).length > 1}
            onCreate={() => setCreating(true)}
          />
        ) : (
          <TaskTable
            items={list.data.items}
            total={list.data.total}
            sort={{ sort: search.sort, dir: search.dir }}
            onSort={(s) => setSearch({ sort: s.sort, dir: s.sort ? s.dir : undefined })}
            cols={cols}
          />
        )}
      </div>
    </div>
  );
}

function EmptyState({
  view,
  filtered,
  onCreate,
}: {
  view: TaskView;
  filtered: boolean;
  onCreate: () => void;
}) {
  const text: Record<TaskView, string> = {
    doing: 'Задач в работе нет.',
    assigned: 'Вы пока никому не ставили задач.',
    watching: 'Вы не наблюдаете ни за одной задачей.',
    all: 'Задач не найдено.',
  };
  return (
    <div className="panel px-6 py-10 text-center">
      <p className="font-medium">{filtered ? 'По этим условиям задач нет.' : text[view]}</p>
      <button type="button" className="link mt-2 text-[13.5px]" onClick={onCreate}>
        Поставить задачу
      </button>
    </div>
  );
}

export function UnreadMark({ count }: { count: number }) {
  if (!count) return null;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-signal px-1.5 text-[11.5px] font-semibold leading-[18px] text-ink tabular-nums"
      title={`Новых комментариев: ${count}`}
    >
      <MessageSquare className="size-3" aria-hidden />
      {count}
      <span className="sr-only">новых комментариев</span>
    </span>
  );
}

/** Групповое действие над выбранными задачами: исполнитель, статус или срок. */
function TaskBulkActions({ ids, onDone }: { ids: string[]; onDone: (r: BulkResult) => void }) {
  const qc = useQueryClient();
  const people = useQuery(assigneesQuery);
  const [action, setAction] = useState<'assignee' | 'status' | 'deadline'>('assignee');
  const [assignee, setAssignee] = useState('');
  const [status, setStatus] = useState<TaskStatus>('done');
  const [deadline, setDeadline] = useState('');
  const apply = useMutation({
    mutationFn: () =>
      api<BulkResult>('/tasks/bulk', {
        method: 'POST',
        body: {
          ids,
          ...(action === 'assignee' && { assigneeId: assignee }),
          ...(action === 'status' && { status }),
          ...(action === 'deadline' && { deadline: deadline ? new Date(deadline).toISOString() : null }),
        },
      }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['tasks'] });
      onDone(r);
    },
  });
  const ready = action === 'assignee' ? Boolean(assignee) : true;
  return (
    <>
      <Select
        aria-label="Что изменить"
        value={action}
        onChange={(e) => setAction(e.target.value as typeof action)}
        className="w-[205px]"
      >
        <option value="assignee">Сменить исполнителя</option>
        <option value="status">Сменить статус</option>
        <option value="deadline">Поставить срок</option>
      </Select>
      {action === 'assignee' && (
        <Select
          aria-label="Новый исполнитель"
          value={assignee}
          onChange={(e) => setAssignee(e.target.value)}
          className="w-[210px]"
        >
          <option value="">Кому передать…</option>
          {people.data?.map((p) => (
            <option key={p.userId} value={p.userId}>
              {p.fullName}
            </option>
          ))}
        </Select>
      )}
      {action === 'status' && (
        <Select
          aria-label="Новый статус"
          value={status}
          onChange={(e) => setStatus(e.target.value as TaskStatus)}
          className="w-[160px]"
        >
          {TASK_STATUSES.filter((s) => s !== 'review').map((s) => (
            <option key={s} value={s}>
              {TASK_STATUS_LABELS[s]}
            </option>
          ))}
        </Select>
      )}
      {action === 'deadline' && (
        <Input
          type="datetime-local"
          aria-label="Новый срок (пусто — без срока)"
          value={deadline}
          onChange={(e) => setDeadline(e.target.value)}
          className="w-[200px]"
        />
      )}
      <Button
        size="sm"
        variant="secondary"
        className="h-8 border-white/40"
        disabled={!ready}
        loading={apply.isPending}
        onClick={() => apply.mutate()}
      >
        {action === 'deadline' && !deadline ? 'Снять срок' : 'Применить'}
      </Button>
      {apply.error && <span className="text-[#ffb4a8]">{apply.error.message}</span>}
    </>
  );
}

type TaskColumn =
  | 'number'
  | 'status'
  | 'deadline'
  | 'assignee'
  | 'creator'
  | 'project'
  | 'created'
  | 'activity';
const TASK_COLUMNS: ColumnDef<TaskColumn>[] = [
  { key: 'number', label: '№' },
  { key: 'status', label: 'Статус' },
  { key: 'deadline', label: 'Срок' },
  { key: 'assignee', label: 'Исполнитель' },
  { key: 'creator', label: 'Постановщик' },
  { key: 'project', label: 'Проект', defaultHidden: true },
  { key: 'created', label: 'Создана', defaultHidden: true },
  { key: 'activity', label: 'Активность' },
];

function TaskTable({
  items,
  total,
  sort,
  onSort,
  cols,
}: {
  items: TaskListItem[];
  total: number;
  sort: SortState<TaskSort>;
  onSort: (s: SortState<TaskSort>) => void;
  cols: ColumnsState<TaskColumn>;
}) {
  const show = cols.show;
  const sel = useSelection(items.map((t) => t.id));
  const [result, setResult] = useState<BulkResult | null>(null);
  const head = (label: string, key: TaskSort) => (
    <SortHeader label={label} sortKey={key} state={sort} onSort={onSort} />
  );
  return (
    <>
      {result && <BulkNotice result={result} onClose={() => setResult(null)} />}
      {sel.count > 0 && (
        <BulkBar count={sel.count} onClear={sel.clear}>
          <TaskBulkActions
            ids={[...sel.selected]}
            onDone={(r) => {
              setResult(r);
              sel.clear();
            }}
          />
        </BulkBar>
      )}
      <div className="panel relative overflow-x-auto">
        <table className="w-full min-w-[900px] text-left">
          <thead>
            <tr className="border-b border-line text-[13px] text-steel">
              <th scope="col" className="w-10 py-2.5 pl-4">
                <SelectBox
                  checked={sel.all}
                  indeterminate={sel.some}
                  onChange={sel.toggleAll}
                  label="Выбрать все задачи на странице"
                />
              </th>
              {show('number') && head('№', 'number')}
              {head('Задача', 'title')}
              {show('status') && head('Статус', 'status')}
              {show('deadline') && head('Срок', 'deadline')}
              {show('assignee') && head('Исполнитель', 'assignee')}
              {show('creator') && head('Постановщик', 'creator')}
              {show('project') && (
                <th scope="col" className="px-4 py-2.5 font-medium">
                  Проект
                </th>
              )}
              {show('created') && head('Создана', 'created')}
              {show('activity') && head('Активность', 'activity')}
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {items.map((t) => (
              <tr key={t.id} className={clsx('hover:bg-paper/70', sel.has(t.id) && 'bg-signal-soft/40')}>
                <td className="py-2.5 pl-4">
                  <SelectBox
                    checked={sel.has(t.id)}
                    onChange={() => sel.toggle(t.id)}
                    label={`Выбрать задачу ${t.number}`}
                  />
                </td>
                {show('number') && <td className="px-4 py-2.5 tabular-nums text-steel">{t.number}</td>}
                <td className="px-4 py-2.5">
                  <Link
                    to="/tasks/$id"
                    params={{ id: t.id }}
                    className={clsx('hover:text-link', t.unreadComments ? 'font-semibold' : 'font-medium')}
                  >
                    {t.title}
                  </Link>
                  <span className="ml-2 inline-flex gap-1.5 align-middle">
                    <PriorityMark priority={t.priority} />
                    <UnreadMark count={t.unreadComments} />
                  </span>
                  {t.project && !show('project') && (
                    <span className="block text-[12.5px] text-steel">{t.project.name}</span>
                  )}
                </td>
                {show('status') && (
                  <td className="px-4 py-2.5">
                    <StatusBadge status={t.status} />
                  </td>
                )}
                {show('deadline') && (
                  <td className="px-4 py-2.5">
                    <Deadline iso={t.deadline} overdue={t.isOverdue} />
                  </td>
                )}
                {show('assignee') && (
                  <td className="px-4 py-2.5">
                    <span className="flex items-center gap-2">
                      <Avatar name={t.assignee.fullName} userId={t.assignee.userId} size={24} />
                      {t.assignee.fullName}
                    </span>
                  </td>
                )}
                {show('creator') && <td className="px-4 py-2.5 text-ink-soft">{t.creator.fullName}</td>}
                {show('project') && (
                  <td className="px-4 py-2.5 text-ink-soft">
                    {t.project?.name ?? <span className="text-steel">—</span>}
                  </td>
                )}
                {show('created') && (
                  <td className="px-4 py-2.5 whitespace-nowrap text-ink-soft">{formatMoment(t.createdAt)}</td>
                )}
                {show('activity') && (
                  <td className="px-4 py-2.5 whitespace-nowrap text-ink-soft">
                    {formatMoment(t.activityAt)}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-[12.5px] text-steel">
        {items.length < total
          ? `Показано ${items.length} из ${total} — уточните фильтр.`
          : `${total} ${plural(total, ['задача', 'задачи', 'задач'])}`}
        . Отметьте задачи галочками, чтобы сменить исполнителя, статус или срок сразу у всех.
      </p>
    </>
  );
}

// ---------- Канбан ----------

export function TaskCard({
  task,
  dragging,
  onDragStart,
  onDragEnd,
}: {
  task: TaskListItem;
  dragging: boolean;
  onDragStart: (e: DragEvent) => void;
  onDragEnd: () => void;
}) {
  return (
    <Link
      to="/tasks/$id"
      params={{ id: task.id }}
      draggable
      data-task-id={task.id}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className={clsx(
        'block rounded-md border border-line bg-surface px-3 py-2.5 transition-shadow hover:border-line-strong hover:shadow-[0_4px_14px_-8px_rgb(24_34_43/0.4)]',
        dragging && 'opacity-40',
        task.isOverdue && 'border-l-[3px] border-l-danger',
      )}
    >
      <p className="flex gap-2 text-[12px] text-steel">
        <span className="tabular-nums">#{task.number}</span>
        {task.project && <span className="ml-auto truncate">{task.project.name}</span>}
      </p>
      <p className="mt-0.5 font-medium leading-snug">{task.title}</p>
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12.5px] text-steel">
        <PriorityMark priority={task.priority} />
        <Deadline iso={task.deadline} overdue={task.isOverdue} compact />
        {task.checklist.total > 0 && (
          <span className="inline-flex items-center gap-1 tabular-nums" title="Чек-лист">
            <CheckSquare className="size-3.5" aria-hidden />
            {task.checklist.done}/{task.checklist.total}
          </span>
        )}
        {task.unreadComments > 0 ? (
          <UnreadMark count={task.unreadComments} />
        ) : task.commentCount > 0 ? (
          <span className="inline-flex items-center gap-1 tabular-nums" title="Комментарии">
            <MessageSquare className="size-3.5" aria-hidden />
            {task.commentCount}
          </span>
        ) : null}
        <span className="ml-auto" title={`Исполнитель: ${task.assignee.fullName}`}>
          <Avatar name={task.assignee.fullName} userId={task.assignee.userId} size={22} />
        </span>
      </div>
    </Link>
  );
}

function Board({ items, filter }: { items: TaskListItem[]; filter: TasksFilter }) {
  const qc = useQueryClient();
  const [dragId, setDragId] = useState<string | null>(null);
  const [target, setTarget] = useState<{ status: TaskStatus; index: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const key = tasksQuery(filter).queryKey;

  const columns = TASK_STATUSES.map((status) => ({
    status,
    items: items.filter((t) => t.status === status).sort((a, b) => a.sort - b.sort || b.number - a.number),
  }));

  const move = useMutation({
    mutationFn: (v: { id: string; status: TaskStatus; afterId?: string; beforeId?: string }) =>
      api(`/tasks/${v.id}/move`, {
        method: 'POST',
        body: { status: v.status, afterId: v.afterId, beforeId: v.beforeId },
      }),
    onError: (err) => {
      setError(err.message);
      window.setTimeout(() => setError(null), 6000);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['tasks'] }),
  });

  function indexAt(e: DragEvent, column: HTMLElement) {
    const cards = [...column.querySelectorAll<HTMLElement>('[data-task-id]')].filter(
      (el) => el.dataset.taskId !== dragId,
    );
    const i = cards.findIndex((el) => {
      const r = el.getBoundingClientRect();
      return e.clientY < r.top + r.height / 2;
    });
    return i === -1 ? cards.length : i;
  }

  function drop(status: TaskStatus) {
    if (!dragId || !target) return;
    const column = columns.find((c) => c.status === status)?.items.filter((t) => t.id !== dragId) ?? [];
    const after = column[target.index - 1];
    const before = column[target.index];
    const dragged = items.find((t) => t.id === dragId);
    if (!dragged) return;
    // Сразу показываем карточку на новом месте; сервер подтвердит или вернёт с объяснением
    const sort =
      after && before
        ? (after.sort + before.sort) / 2
        : after
          ? after.sort + 1
          : before
            ? before.sort - 1
            : dragged.sort;
    qc.setQueryData(key, (old: Paginated<TaskListItem> | undefined) =>
      old ? { ...old, items: old.items.map((t) => (t.id === dragId ? { ...t, status, sort } : t)) } : old,
    );
    move.mutate({ id: dragId, status, afterId: after?.id, beforeId: before?.id });
    setDragId(null);
    setTarget(null);
  }

  return (
    <div>
      {error && (
        <div
          role="alert"
          className="mb-3 rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-[13.5px] text-danger"
        >
          {error}
        </div>
      )}
      <div className="-mx-4 overflow-x-auto px-4 pb-2 lg:-mx-8 lg:px-8">
        <div className="grid min-w-[1100px] grid-cols-5 gap-3">
          {columns.map((col) => (
            <section
              key={col.status}
              aria-label={TASK_STATUS_LABELS[col.status]}
              onDragOver={(e) => {
                if (!dragId) return;
                e.preventDefault();
                setTarget({ status: col.status, index: indexAt(e, e.currentTarget) });
              }}
              onDragLeave={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node)) setTarget(null);
              }}
              onDrop={(e) => {
                e.preventDefault();
                drop(col.status);
              }}
              className={clsx(
                'flex min-h-[320px] flex-col rounded-[var(--radius-panel)] bg-sunken/70 p-2',
                target?.status === col.status && 'outline-2 outline-dashed outline-line-strong',
              )}
            >
              <header className="flex items-center gap-2 px-1.5 pb-2 pt-1">
                <span aria-hidden className={clsx('size-2 rounded-full', STATUS_TONE[col.status].dot)} />
                <h2 className="text-[13.5px] font-semibold">{TASK_STATUS_LABELS[col.status]}</h2>
                <span className="text-[13px] tabular-nums text-steel">{col.items.length}</span>
              </header>
              <div className="flex flex-col gap-2">
                {(() => {
                  // Перетаскиваемая карточка остаётся на месте полупрозрачной (скрытие источника браузер может
                  // принять за отмену перетаскивания), а позиции считаются без неё — так же, как в indexAt
                  let visibleIndex = 0;
                  return col.items.map((t) => {
                    const lifted = t.id === dragId && target !== null;
                    const i = lifted ? -1 : visibleIndex++;
                    return (
                      <div key={t.id}>
                        {target?.status === col.status && target.index === i && (
                          <div aria-hidden className="mb-2 h-1 rounded bg-signal" />
                        )}
                        <TaskCard
                          task={t}
                          dragging={dragId === t.id}
                          onDragStart={(e) => {
                            e.dataTransfer.effectAllowed = 'move';
                            e.dataTransfer.setData('text/plain', t.id);
                            setDragId(t.id);
                          }}
                          onDragEnd={() => {
                            setDragId(null);
                            setTarget(null);
                          }}
                        />
                      </div>
                    );
                  });
                })()}
                {target?.status === col.status &&
                  dragId &&
                  target.index >= col.items.filter((t) => t.id !== dragId).length && (
                    <div aria-hidden className="h-1 rounded bg-signal" />
                  )}
                {col.items.length === 0 && !target && (
                  <p className="px-1.5 py-6 text-center text-[12.5px] text-steel">Пусто</p>
                )}
              </div>
            </section>
          ))}
        </div>
      </div>
      <p className="mt-2 text-[12.5px] text-steel">
        Перетащите карточку в другую колонку, чтобы сменить статус. В «Завершена» — задачи за последние две
        недели.
      </p>
    </div>
  );
}
